import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";

const FALLBACK_API_BASE = `${window.location.origin}/api`;
const API_URL = import.meta.env.VITE_API_URL || FALLBACK_API_BASE;
const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

function fmtTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function MessageBubble({ message }) {
  const isVisitor = message.from === "visitor";
  const isHuman = message.from === "human";
  const attachment = message.attachmentUrl || "";

  return (
    <div className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isVisitor
            ? "bg-gray-800 text-gray-100"
            : isHuman
              ? "bg-blue-600 text-white"
              : "bg-gray-900 border border-gray-800 text-gray-400"
        }`}
      >
        <div className="text-[10px] uppercase tracking-wide opacity-60 mb-1">
          {isHuman ? "You" : isVisitor ? "Visitor" : "AI"}
        </div>
        {message.bugLabel && (
          <div className="text-xs font-semibold text-amber-300 mb-2">
            Bug: {message.bugLabel}
          </div>
        )}
        {attachment ? (
          <a href={attachment} target="_blank" rel="noreferrer" className="block">
            <img src={attachment} alt="Screenshot" className="max-h-48 rounded-lg mt-1" />
          </a>
        ) : (
          <div className="whitespace-pre-wrap break-words">{message.text}</div>
        )}
        <div className="text-[10px] opacity-50 mt-2">{fmtTime(message.createdAt)}</div>
      </div>
    </div>
  );
}

export default function SupportAgentPage() {
  const { shortId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [thread, setThread] = useState({ session: null, messages: [] });
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);
  const replyRef = useRef(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchThread = useCallback(async () => {
    if (!shortId || !token) {
      setError("This support link is missing its access token.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/support/agent/sessions/${shortId}`, {
        headers: authHeaders(),
        params: { token },
      });
      setThread({
        session: res.data?.data?.session || null,
        messages: res.data?.data?.messages || [],
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.msg || "This support link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  }, [shortId, token, authHeaders]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  useEffect(() => {
    if (!shortId || !token) return;

    const socket = io(SOCKET_URL, {
      auth: { operatorToken: token },
      query: { shortId, operatorToken: token },
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("chat:message", () => fetchThread());
    socket.on("support:operator:update", () => fetchThread());
    socket.emit("support:presence");

    const presence = setInterval(() => {
      socket.emit("support:presence");
      axios
        .post(
          `${API_URL}/support/agent/sessions/${shortId}/presence`,
          {},
          { headers: authHeaders(), params: { token } }
        )
        .catch(() => {});
    }, 30000);

    socketRef.current = socket;
    return () => {
      clearInterval(presence);
      socket.disconnect();
    };
  }, [shortId, token, authHeaders, fetchThread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread.messages]);

  useEffect(() => {
    if (!loading && !error) {
      replyRef.current?.focus();
    }
  }, [loading, error]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending || thread.session?.status === "closed") return;

    setSending(true);
    try {
      if (socketRef.current?.connected) {
        socketRef.current.emit("support:operator:reply", { text });
      } else {
        await axios.post(
          `${API_URL}/support/agent/sessions/${shortId}/reply`,
          { text },
          { headers: authHeaders(), params: { token } }
        );
      }
      setReply("");
      await fetchThread();
    } catch (err) {
      console.error("reply failed", err);
    } finally {
      setSending(false);
    }
  };

  const closeSession = async () => {
    await axios.patch(
      `${API_URL}/support/agent/sessions/${shortId}`,
      { status: "closed" },
      { headers: authHeaders(), params: { token } }
    );
    await fetchThread();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-300 flex items-center justify-center">
        Loading conversation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-300 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-semibold text-white">Support link unavailable</h1>
          <p className="text-sm text-gray-400">{error}</p>
          <p className="text-xs text-gray-500">
            Request a fresh link from the support alert email, or open Command Center if you are signed in as admin.
          </p>
        </div>
      </div>
    );
  }

  const session = thread.session;
  const isClosed = session?.status === "closed";

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col">
      <header className="border-b border-gray-800 px-4 py-4 flex items-center justify-between gap-4 shrink-0">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-blue-400 font-semibold mb-1">
            Support console
          </p>
          <h1 className="text-lg font-semibold text-white">
            Chat with {session?.userEmail || "anonymous visitor"}
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            {session?.userPlan ? `${session.userPlan} plan · ` : ""}
            session <span className="font-mono text-gray-500">[{shortId}]</span>
            {connected ? (
              <span className="ml-2 text-green-400">· live</span>
            ) : (
              <span className="ml-2 text-amber-400">· reconnecting</span>
            )}
          </p>
        </div>
        {!isClosed && (
          <button
            type="button"
            onClick={closeSession}
            className="text-xs px-3 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 shrink-0"
          >
            Close chat
          </button>
        )}
      </header>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-4 w-full max-w-3xl mx-auto"
      >
        {thread.messages.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-16">
            No messages yet. When the visitor writes, their messages will appear here.
          </div>
        )}
        {thread.messages.map((m, i) => (
          <MessageBubble key={`${m.createdAt || i}-${m.from}`} message={m} />
        ))}
      </div>

      {isClosed ? (
        <div className="border-t border-gray-800 p-4 text-center text-sm text-gray-500 shrink-0">
          This conversation is closed.
        </div>
      ) : (
        <div className="border-t border-gray-800 p-4 shrink-0 bg-gray-950">
          <div className="flex gap-3 max-w-3xl mx-auto w-full">
            <textarea
              ref={replyRef}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              placeholder="Write your reply to the visitor…"
              className="flex-1 text-sm rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 resize-none outline-none focus:border-blue-500 text-gray-100"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendReply();
                }
              }}
            />
            <button
              type="button"
              disabled={sending || !reply.trim()}
              onClick={sendReply}
              className="self-end px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-blue-500"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
